/**
 * @module Experince/Product-Selector
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */

// tslint:disable:max-file-line-count
// Core Libraries
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Subject } from 'rxjs';

// Interfaces
import { ProductSelectorConfigData } from '@core/aem/interfaces/product-selector/product-selector.interface';

// Models
import { TileManagementService } from '@support/shared/services/tile-management.service';
import {
  LineDeivceDetails,
  lineTypeEnum,
} from '@experience/shop/product-selector/interfaces/product-selector.interface';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { CART_ITEM_STATUS } from '@support/cart-support/constants/cart-support-service-constants';
import { PERMISSION_CONSTANTS } from '@support/shared/constants/permission.constants';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'tmo-line-summary',
  templateUrl: './line-summary.component.html',
})
export class LineSummaryComponent implements OnInit, OnDestroy {
  @Input() public aemContent: ProductSelectorConfigData;
  /**
   *
   */
  public hasRepPermissionForMultiLine: boolean;
  /**
   * Total number of lines selected
   */
  public formattedTotalNumberOfLines: string;

  /**
   * Stores the boolean to show aal the products
   */
  public seeAll: boolean;
  /**
   * Holds true to show line needs a device selection
   */
  public cartHasOnlyDevice: boolean;
  /**
   * Holds true to show line needs a plan selection
   */
  public cartHasOnlyPlan: boolean;
  /**
   * Holds true to show lline needs plan and device selection
   */
  public needPlanAndDevice: boolean;
  /**
   * Holds selected plan and device details
   */
  public updatedLineDetails: LineDeivceDetails;

  public lineSummaryModels: LineDeivceDetails[];

  public nonLineSummaryModels: LineDeivceDetails[];
  /**
   * Unsubscriber for all subscribtions
   */
  private ngUnsubscribe: Subject<void>;

  public totalLinesCount: number;
  /**
   * Holds e-num for existing customer
   */
  public isExisting: number;
  constructor(
    private tileManagementService: TileManagementService,
    private objectService: ObjectService,
    private cartSupportService: CartSupportService,
    private commonUtilsService: CommonUtilService
  ) {
    this.ngUnsubscribe = new Subject<void>();
  }

  public ngOnInit(): void {
    this.totalLinesCount = 0;
    this.isExisting = lineTypeEnum.EXISTING;
    this.getAllLineNonLineItemDetails();
    this.getUpdatedFocusedTileDetails();
    this.tileManagementService.getUpdatedData();
    this.aemContent.deviceNeedsAPlan = this.commonUtilsService.messageFormatter(
      this.aemContent.deviceNeedsAPlan,
      ['1']
    );
    this.hasRepPermissionForMultiLine = this.cartSupportService.checkForAddALinePermission(
      PERMISSION_CONSTANTS.PRODUCT_SELECTOR,
      PERMISSION_CONSTANTS.ADD_A_LINE
    );
    this.aemContent.lineNeedsADevice = this.commonUtilsService.messageFormatter(
      this.aemContent.lineNeedsADevice,
      ['1']
    );
    this.aemContent.lineNeedsPlanAndDeviceSelection = this.commonUtilsService.messageFormatter(
      this.aemContent.lineNeedsPlanAndDeviceSelection,
      ['1']
    );
  }

  /**
   * Destroys all the subscriptions
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Function to update line summary
   */
  // tslint:disable cyclomatic-complexity
  public updateLineSummary(): void {
    const isPlanSelected =
      this.objectService.get(this.updatedLineDetails, 'noPlanNeeded') ||
      this.objectService.get(this.updatedLineDetails, 'plansFromCart.planOfferName');
    const isDeviceSelected =
      this.objectService.get(this.updatedLineDetails, 'selectedDevice.id') ||
      this.objectService.get(this.updatedLineDetails, 'byodSimData') ||
      this.objectService.get(this.updatedLineDetails, 'byodCMMOrImei');
    const isExistingCustomer = this.updatedLineDetails.lineTileType === this.isExisting;
    this.cartHasOnlyDevice = !isPlanSelected && !!isDeviceSelected && !isExistingCustomer;
    this.cartHasOnlyPlan = !!isPlanSelected && !isDeviceSelected && !isExistingCustomer;
    this.needPlanAndDevice = !isPlanSelected && !isDeviceSelected && !isExistingCustomer;
  }

  /**
   * Function holds all line and non line details
   */
  private getAllLineNonLineItemDetails(): void {
    this.tileManagementService.getLineNonLineItemDetails
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((data: LineDeivceDetails[]) => {
        const groupValues: LineDeivceDetails = this.commonUtilsService.groupBy(
          data,
          'lineTileType'
        );
        const newLines = groupValues[lineTypeEnum.LINE] || [];
        this.nonLineSummaryModels = groupValues[lineTypeEnum.NONLINE] || [];
        const existingLines = groupValues[lineTypeEnum.EXISTING];
        const updatedExistingLines =
          existingLines &&
          existingLines.filter((existingLine: LineDeivceDetails) => {
            return (
              this.objectService.get(existingLine, `selectedDevice.actionCode`) ===
              CART_ITEM_STATUS.ITEM_ADD
            );
          });
        this.lineSummaryModels = updatedExistingLines
          ? newLines.concat(updatedExistingLines)
          : newLines;
        this.totalLinesCount = this.lineSummaryModels.length;
      });
  }

  /**
   * Function used to get focused tile id details.
   */
  private getUpdatedFocusedTileDetails(): void {
    this.tileManagementService.focusedTileDetails
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((data: LineDeivceDetails) => {
        if (data) {
          this.updatedLineDetails = data;
          this.updateLineSummary();
        }
      });
  }
}
